import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useTrans } from "../../../hooks";
import { Box, VStack, Divider, Button, IconButton } from "@chakra-ui/react";
import {
    MapContainer,
    TileLayer,
    useMapEvents,
    MapContainerProps,
    Marker,
    Popup,
    useMap,
} from "react-leaflet";
import L from "leaflet";
import { AppGeoSearch, AppIcon, MapLocation } from "../../../components";
import {
    Company,
    Internship,
    Partnership,
    School,
    Service,
    Typology,
} from "../../../apis";
import { APPC, cPath } from "../../../config";
import MarkerClusterGroup from "react-leaflet-cluster";
import "react-leaflet-cluster/dist/assets/MarkerCluster.css";
import "react-leaflet-cluster/dist/assets/MarkerCluster.Default.css";

const PARTNERSHIPSTATUS = APPC.BE.Partnership.STATUS;

const defaultMapContainerProps: MapContainerProps = {
    zoom: 15,
    minZoom: 12,
    maxZoom: 18,
    scrollWheelZoom: true,
    style: { height: "100%", width: "100%" },
};

const getMarkerByColor = (color: string) =>
    new L.Icon({
        iconUrl: `https://raw.githubusercontent.com/pointhi/leaflet-color-markers/master/img/marker-icon-2x-${color}.png`,
        shadowUrl:
            "https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png",
        iconSize: [25, 41],
        iconAnchor: [12, 41],
        popupAnchor: [1, -34],
        shadowSize: [41, 41],
    });

const getMarkerColorByStatus = (partnershipStatus: string) => {
    switch (partnershipStatus) {
        case PARTNERSHIPSTATUS.STATUS_APPROVED:
            return "green";

        case PARTNERSHIPSTATUS.STATUS_PENDING:
            return "yellow";

        case PARTNERSHIPSTATUS.STATUS_REJECTED:
            return "red";

        default:
            return "blue";
    }
};

export interface AppFindSchoolInternshipMapProps {
    list: Internship[];
    partnershipStatusList: Record<string, Partnership>;
    activeSchoolOrCompany: Company | School | null;
    onCenterChange: (lat: number, lng: number) => void;
    mapProps?: MapContainerProps;
}

export const AppFindSchoolInternshipMap = ({
    list,
    partnershipStatusList,
    activeSchoolOrCompany,
    onCenterChange,
    mapProps,
}: AppFindSchoolInternshipMapProps) => {
    const { t } = useTrans();
    const navigate = useNavigate();

    const [latLng, setLatLng] = useState<[number, number]>([
        48.8575475, 2.3513765,
    ]);
    const center = mapProps?.center;

    const MapEvents = () => {
        useMapEvents({
            moveend: (e) => {
                const center = e.target.getCenter();
                onCenterChange(center.lat, center.lng);
            },
            zoomend: (e) => {
                const center = e.target.getCenter();
                onCenterChange(center.lat, center.lng);
            },
        });
        return null;
    };

    const ChangeView = ({ center }: { center: L.LatLngExpression }) => {
        const map = useMap();

        useEffect(() => {
            map.setView(center);
        }, [center, map]);

        return null;
    };

    useEffect(() => {
        if (
            activeSchoolOrCompany?.latitude &&
            activeSchoolOrCompany?.longitude
        ) {
            setLatLng([
                Number(activeSchoolOrCompany.latitude),
                Number(activeSchoolOrCompany.longitude),
            ]);
        }
    }, [activeSchoolOrCompany]);

    return (
        <Box w="full" h="full" position="relative">
            <MapContainer
                {...defaultMapContainerProps}
                {...mapProps}
                attributionControl={false}
            >
                <TileLayer
                    attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors'
                    url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
                />
                {center && <ChangeView center={center} />}
                <MapEvents />
                <AppGeoSearch
                    onSelect={(location: MapLocation) => {
                        onCenterChange(location.lat, location.lng);
                    }}
                />
                <MarkerClusterGroup>
                    {list.map((internship) => {
                        const company = internship.company as Company;
                        const lat = Number(company.latitude);
                        const lng = Number(company.longitude);
                        if (!lat || !lng) {
                            return null;
                        }
                        const partnershipStatus = Object.keys(
                            partnershipStatusList
                        ).includes(String(company?.id))
                            ? partnershipStatusList[company?.id]?.status
                            : "";
                        const markerColor =
                            getMarkerColorByStatus(partnershipStatus);

                        return (
                            <Marker
                                key={company.id}
                                position={[lat, lng]}
                                icon={getMarkerByColor(markerColor)}
                            >
                                <Popup autoPan={false}>
                                    <Box textStyle={"boldSm"} mb={2}>
                                        {company.name}
                                    </Box>
                                    <Divider
                                        mb={2}
                                        borderColor={
                                            "var(--chakra-colors-primaryT80)"
                                        }
                                    />
                                    <VStack
                                        w="full"
                                        justifyContent="center"
                                        alignItems="flex-start"
                                        spacing={1}
                                    >
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Internship:name.label")}: `}</strong>
                                            {internship.name}
                                        </Box>
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Typology:name.label")}: `}</strong>
                                            {
                                                (
                                                    internship?.typology as Typology
                                                )?.name
                                            }
                                        </Box>
                                        <Box textStyle={"mediumSm"}>
                                            <strong>{`${t("ent.Service:name.label")}: `}</strong>
                                            {
                                                (internship?.service as Service)
                                                    ?.name
                                            }
                                        </Box>
                                        {!partnershipStatus && (
                                            <Button
                                                size="xs"
                                                mt={1.5}
                                                onClick={() => {
                                                    navigate(
                                                        `${cPath(
                                                            "ADMIN.CREATE_PARTNERSHIP_COMPANY_PAGE_REQUEST"
                                                        )}?q=${encodeURI(company.name)}`
                                                    );
                                                }}
                                            >
                                                {t(
                                                    "padm.PartnershipCompanyPage:button.createOnMap"
                                                )}
                                            </Button>
                                        )}
                                    </VStack>
                                </Popup>
                            </Marker>
                        );
                    })}
                </MarkerClusterGroup>
            </MapContainer>
            <Box position="absolute" top="128px" left="11px">
                <IconButton
                    w={"32px"}
                    h={"32px"}
                    variant={"secondary"}
                    border={"1px solid var(--chakra-colors-greyT60)"}
                    backgroundColor={"var(--chakra-colors-white)"}
                    boxShadow="xl"
                    aria-label="back to location"
                    icon={
                        <AppIcon
                            name="ics-location-gps-fixed"
                            w="1.1rem"
                            color="var(--chakra-colors-black)"
                        />
                    }
                    onClick={() => {
                        onCenterChange(latLng[0], latLng[1]);
                    }}
                />
            </Box>
        </Box>
    );
};
